import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import StyChip from 'routes/HumanResourcesModule/components/ui/StyChip';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'helpers/dateHelper';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { START_DATE } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { useAgentArrivalHypothesisCRUDMutations } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/hooks/agentArrivalHypothesis';
import { StyChipLoading } from 'routes/HumanResourcesModule/components/ui/StyChipLoading';
import {
    AgentHypothesesCellRenderValue,
    SimulationAgentData,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import {
    AgentArrivalHypothesisType,
    AgentArrivalHypothesisTypesColor,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/constants';
import {
    AgentArrivalHypothesis,
    DetailedAgentArrivalHypothesis,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/types';
import { useGetOfficialGradesOfLocalStorage } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/hooks/agentsSimulation';
import { AGENT_SOFTWARE_ID } from 'routes/HumanResourcesModule/business/definitions';
import AgentArrivalHypothesisUpdateModal from '../AgentArrivalHypothesisModal/AgentArrivalHypothesisUpdateModal';
import { getAgentArrivalHypothesisDisplayType } from '../../../helpers/gridHelpers';

const ensureValueType = (
    value: AgentArrivalHypothesis | DetailedAgentArrivalHypothesis,
    isUpdateEnabled: boolean,
): value is AgentArrivalHypothesis => isUpdateEnabled;

const getArrivalHypothesisChipLabel = (
    startDate: SimulationDateString,
    t: TranslationT,
): string => {
    const hypothesisType = t(
        `humanResources.simulation.agentArrivalHypothesisType.${AgentArrivalHypothesisType.SIMPLE_ARRIVAL}`,
    );
    const hypothesisDate = startDate ? formatDate(new Date(startDate)) : '';
    return t('humanResources.simulation.agentHypothesisChipLabel', {
        hypothesisType,
        hypothesisDate,
    });
};
type Props = CellRendererParams<
    SimulationAgentData, // type of params.data
    AgentHypothesesCellRenderValue | DetailedAgentArrivalHypothesis // type of params.value
> & {
    hrSimulation: HrSimulation;
    isUpdateEnabled: boolean;
};

const UpdateAgentArrivalHypothesisCellRenderer = ({
    value,
    data,
    hrSimulation,
    isUpdateEnabled = true,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const agentArrivalHypothesis =
        value && 'agentArrivalHypothesis' in value
            ? value?.agentArrivalHypothesis
            : value;

    const echelonGrades = useGetOfficialGradesOfLocalStorage();

    // In case the value is defined, we already have an hypothesis for this agent
    // we display it with the possibility to modify it
    const displayType = getAgentArrivalHypothesisDisplayType();
    const agentSoftwareId = data?.[AGENT_SOFTWARE_ID];

    const chipColor = AgentArrivalHypothesisTypesColor[displayType];
    const chipIcon = isUpdateEnabled ? <EditIcon /> : <></>;

    const { editHypothesis, deleteHypothesis, isHypothesisMutationLoading } =
        useAgentArrivalHypothesisCRUDMutations({
            simulationId: hrSimulation.id,
            hypothesisId: agentArrivalHypothesis?.id,
            agentSoftwareId,
        });

    if (!agentArrivalHypothesis) return <></>;
    return (
        <>
            {isHypothesisMutationLoading ? (
                <StyChipLoading />
            ) : (
                <StyChip
                    label={getArrivalHypothesisChipLabel(
                        agentArrivalHypothesis[START_DATE],
                        t,
                    )}
                    onClick={() => setShowUpdateModal(true)}
                    icon={chipIcon}
                    styledColor={chipColor}
                />
            )}
            {showUpdateModal &&
                data &&
                ensureValueType(agentArrivalHypothesis, isUpdateEnabled) && (
                    <AgentArrivalHypothesisUpdateModal
                        deleteHypothesis={deleteHypothesis}
                        editHypothesis={editHypothesis}
                        hrSimulation={hrSimulation}
                        hypothesis={agentArrivalHypothesis}
                        officialGrades={echelonGrades}
                        onClose={() => setShowUpdateModal(false)}
                        show={showUpdateModal}
                    />
                )}
        </>
    );
};

export default UpdateAgentArrivalHypothesisCellRenderer;
